import { useState, useEffect, Fragment } from "react";
import {
  IFoundIndividualLicenseForInvoice,
  IFoundSiteLicenseForInvoice,
  ILicenseSearchResult,
} from "../../utils/quoteUtils";
import InvoicePageCard from "./InvoicePageCard";
import SiteLicenseCard from "./SiteLicenseCard";
import IndividualLicensesCard from "./IndividualLicenseCard";
import { isBefore, sub } from "date-fns";
import { useActivationContext } from "./LicenseActivationContext";

/**
 *
 * This component contains information about existing license info for a the site being quoted. The info displayed depends on the type of quote. A district quote show info if the quoted district has a district license. A school quote will find an existing school license but will also show info about a district license which currently covers that school. An individual quote will show individual licenses and site licenses associated w/ the purchaser being quoted. The match on for site licenses in the individual case is not perfect and depends on the purchaser name matching the name of the school/district as it exists in our database.
 */
const LicenseDetails: React.FC<{
  initialLicenseData: ILicenseSearchResult | undefined;
}> = ({ initialLicenseData }) => {
  const PAST_TIME_THRESHOLD = { months: 6 };

  const past_date_threshold = sub(new Date(), PAST_TIME_THRESHOLD);

  const [licenseData, setLicenseData] = useState<
    ILicenseSearchResult | undefined
  >(undefined);
  const [schoolLicense, setSchoolLicense] = useState<
    IFoundSiteLicenseForInvoice | undefined
  >(undefined);
  const [districtLicense, setDistrictLicense] = useState<
    IFoundSiteLicenseForInvoice | undefined
  >(undefined);
  const [individualLicenses, setIndividualLicenses] = useState<
    IFoundIndividualLicenseForInvoice[] | undefined
  >(undefined);
  const [noSiteLicenses, setNoSiteLicenses] = useState<boolean>(false);
  const [showIndividualNotFound, setShowIndividualNotFound] =
    useState<boolean>(false);

  const activationContext = useActivationContext();

  useEffect(() => {
    setLicenseData(initialLicenseData);
    setSchoolLicense(undefined);
    setDistrictLicense(undefined);
    setIndividualLicenses(undefined);
    setNoSiteLicenses(false);
  }, [initialLicenseData]);

  useEffect(() => {
    let noSiteLicensesInData = true;
    if (
      (licenseData?.district?.status === "expired" &&
        !isBefore(
          new Date(licenseData.district.expiration),
          past_date_threshold
        )) ||
      licenseData?.district?.status === "active"
    ) {
      setDistrictLicense(licenseData.district);
      noSiteLicensesInData = false;
    }

    if (
      (licenseData?.school?.status === "expired" &&
        !isBefore(
          new Date(licenseData.school.expiration),
          past_date_threshold
        )) ||
      licenseData?.school?.status === "active"
    ) {
      setSchoolLicense(licenseData.school);
      noSiteLicensesInData = false;
    }

    let individualLicenses: IFoundIndividualLicenseForInvoice[] = [];
    if (licenseData?.individual?.integral?.length) {
      individualLicenses = licenseData.individual.integral
        .filter(
          (license) =>
            !isBefore(new Date(license.expiration), past_date_threshold)
        )
        .concat(individualLicenses);
    }
    if (licenseData?.individual?.plus?.length) {
      individualLicenses = licenseData.individual.plus
        .filter(
          (license) =>
            !isBefore(new Date(license.expiration), past_date_threshold)
        )
        .concat(individualLicenses);
    }
    if (individualLicenses.length) {
      setIndividualLicenses(individualLicenses);
    }
    if (
      activationContext.invoiceDetails?.type === "individual" &&
      (licenseData?.individual?.none || !individualLicenses.length)
    ) {
      setShowIndividualNotFound(true);
    } else {
      setShowIndividualNotFound(false);
    }
    setNoSiteLicenses(noSiteLicensesInData);
  }, [licenseData]);

  //A note on hadling when no license of the quoted type is found: for site licenses, that logic in handled in those cards b/c it's simple. Individual is a little more complicated just in terms of keeping integral/plus tracked and what is returned from Node depending on what exists so it's easier to handle here/
  return (
    <Fragment>
      {noSiteLicenses &&
        activationContext.invoiceDetails?.type !== "individual" && (
          <InvoicePageCard title="Current License Details">
            <div className="text-xl">No active site licenses found.</div>
          </InvoicePageCard>
        )}
      {schoolLicense && (
        <SiteLicenseCard licenseDetails={schoolLicense} siteType="school" />
      )}
      {districtLicense && (
        <SiteLicenseCard licenseDetails={districtLicense} siteType="district" />
      )}
      {individualLicenses && individualLicenses.length && (
        <IndividualLicensesCard licenseDetails={individualLicenses} />
      )}
      {showIndividualNotFound && (
        <InvoicePageCard title="Individual Licenses">
          <div className="text-xl">No current Individual licenses found.</div>
        </InvoicePageCard>
      )}
    </Fragment>
  );
};

export default LicenseDetails;
